import React, { useState, useEffect, useRef } from "react"
import { debounce } from "lodash"

interface Props {
  value: string
  onChange: (val: string) => void
  fetchSuggestions: (query: string) => Promise<string[]>
  defaultSuggestions?: string[]
  placeholder?: string
  onBlur?: () => void
  onFocus?: () => void
}

const DebouncedSearchDropdown: React.FC<Props> = ({
  value,
  onChange,
  fetchSuggestions,
  defaultSuggestions = [],
  placeholder = "Search...",
  onBlur,
  onFocus,
}) => {
  const [inputValue, setInputValue] = useState(value || "")
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [hasFocused, setHasFocused] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [additionalDialogs, setAdditionalDialogs] = useState<string>("")

  const inputRef = useRef<HTMLInputElement | null>(null)
  const cacheRef = useRef<Map<string, string[]>>(new Map())
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const debouncedFetchRef = useRef(
    debounce(async (query: string) => {
      if (query.trim()?.length < 1) {
        setSuggestions(defaultSuggestions)
        setShowDropdown(true)
        setAdditionalDialogs("")
        return
      }
      if (query.trim()?.length > 0 && query.trim()?.length < 3) {
        setAdditionalDialogs("Type min 3 letters")
        setShowDropdown(false)
        setSuggestions([])
        return
      }
      setAdditionalDialogs("")
      if (cacheRef.current.has(query)) {
        setSuggestions(cacheRef.current.get(query)!)
        setShowDropdown(true)
        return
      }
      const results = await fetchSuggestions(query)
      cacheRef.current.set(query, results)
      setSuggestions(results)
      if (results.length === 0)
        setAdditionalDialogs(
          "No results found. Please search your nearest city.",
        )
      setShowDropdown(true)
    }, 200),
  )

  useEffect(() => {
    if (!hasFocused) return
    debouncedFetchRef.current(inputValue)
    return () => debouncedFetchRef.current.cancel()
  }, [inputValue, hasFocused])

  const handleSelect = (item: string) => {
    setInputValue(item)
    onChange(item)
    setShowDropdown(false)
    setIsFullScreen(false)
    setHasFocused(false)
  }

  const handleFocus = () => {
    onFocus?.()
    setHasFocused(true)
    setIsFullScreen(true)
    if (inputValue.trim().length >= 3) {
      debouncedFetchRef.current(inputValue)
    } else {
      setSuggestions(defaultSuggestions)
      setShowDropdown(true)
    }
  }

  const renderSuggestions = () => (
    <ul className="overflow-y-auto max-h-full border border-[#DBDBDB] rounded">
      {suggestions.map((item, idx) => (
        <li
          key={idx}
          onClick={() => handleSelect(item)}
          className={`p-2 text-[#616161] hover:bg-[#736EFF] hover:text-white cursor-pointer ${
            idx === suggestions.length - 1 ? "border-none" : "border-b"
          } border-[#DBDBDB]`}
        >
          {item}
        </li>
      ))}
    </ul>
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
        setIsFullScreen(false)
        setHasFocused(false)
        setAdditionalDialogs("")
        if (!suggestions.includes(inputValue)) {
          setInputValue(value)
        }
        onBlur?.()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [inputValue, suggestions, value])

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {isFullScreen && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col p-4 md:hidden">
          <img
            src="https://ik.imagekit.io/onsnhxjshmp/arrow_left_cyNOOe4ocQ.svg"
            alt="arrow"
            height={24}
            width={24}
            onClick={() => {
              setHasFocused(false)
              setIsFullScreen(false)
              setShowDropdown(false)
              setAdditionalDialogs("")
              if (!suggestions.includes(inputValue)) {
                setInputValue(value)
              }
            }}
          />
          <input
            ref={inputRef}
            autoFocus
            type="text"
            value={inputValue}
            onChange={(e) => {
              const val = e.target.value
              setInputValue(val)
            }}
            placeholder={placeholder}
            className="w-full mt-6 mb-3 z-20 p-2 border border-[#CBCAD5] rounded min-h-[44px] focus:border-primary text-[#616161] placeholder:text-[#A5A4B6]"
            onFocus={handleFocus}
          />
          {additionalDialogs ? (
            <div className="border-[0.5px] rounded -mt-4 p-3 border-[#E8E8E8] text-xs text-[#BDBDBD] text-center">
              {additionalDialogs}
            </div>
          ) : (
            showDropdown && suggestions.length > 0 && renderSuggestions()
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => {
          const val = e.target.value
          setInputValue(val)
        }}
        placeholder={placeholder}
        className="w-full p-2 z-20 border border-[#CBCAD5] rounded min-h-[44px] focus:border-primary text-[#616161] placeholder:text-[#A5A4B6]"
        onFocus={handleFocus}
      />

      {additionalDialogs ? (
        <div className="absolute w-full p-2 text-center z-10 text-[#BDBDBD] border border-[#DBDBDB] bg-white rounded shadow max-h-60 overflow-y-auto bottom-[100%] mb-1">
          {additionalDialogs}
        </div>
      ) : (
        showDropdown &&
        suggestions.length > 0 && (
          <ul
            className={`absolute w-full z-10 border border-[#DBDBDB] bg-white rounded shadow max-h-60 overflow-y-auto bottom-[100%] mb-1`}
          >
            {suggestions.map((item, idx) => (
              <li
                key={idx}
                onClick={() => handleSelect(item)}
                className="p-2 text-[#616161] hover:bg-[#736EFF] hover:text-white cursor-pointer"
              >
                {item}
              </li>
            ))}
          </ul>
        )
      )}

      <style jsx global>{`
        @media only screen and (max-width: 768px) {
          body {
            overflow: ${isFullScreen ? "hidden" : "auto"};
          }
        }
      `}</style>
    </div>
  )
}

export default DebouncedSearchDropdown
